import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import {
  Page,
  NamedLink,
  LayoutSingleColumn,
  CartCard,
  PageContainer,
  PageHeader,
  Card,
  Notice,
  EmptyState,
  LoadingPlaceholder,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import CartButton from './CartButton';
import { sellerVacationOfGroup } from './cartHelpers';
import {
  deleteItemFromCart,
  saveCartQuantity,
  saveItemForLater,
  selectCartShops,
  selectCartSummary,
} from './CartPage.duck';
import { formatVacationUntil } from '../../util/vacation';
import { isScrollingDisabled } from '../../ducks/ui.duck.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';

import css from './CartPage.module.css';
import { handleSubmit } from '../ListingPage/ListingPage.shared.js';
import { createSlug } from '../../util/urlHelpers.js';
import { useRouteConfiguration } from '../../context/routeConfigurationContext.js';
import { useConfiguration } from '../../context/configurationContext.js';
import { getDeliveryMethod } from '../../util/dataExtractors.js';
import { profileSlug, userDisplayNameAsString } from '../../util/data.js';
import { formatMoney } from '../../util/currency';

const HEADING_ID = 'cart-page-heading';
const titleIdOf = listingId => `cart-item-title-${listingId}`;
const IMAGE_SIZES = '(max-width: 1023px) 88px, 112px';

const BagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M5 8h14l-1 12H6L5 8Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </svg>
);

const InfoIcon = () => (
  <svg
    className={css.noteIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg
    className={css.protectionIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3Z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

/** A seller on vacation: why their items can't be checked out yet. */
const SellerAwayNote = ({ id, vacation, name, intl }) => {
  if (!vacation) return null;
  const date = formatVacationUntil(intl, vacation);
  return (
    <p id={id} className={css.sellerAway} role="status">
      {date ? (
        <FormattedMessage id="CartPage.sellerAwayUntil" values={{ name, date }} />
      ) : (
        <FormattedMessage id="CartPage.sellerAway" values={{ name }} />
      )}
    </p>
  );
};

/** Every shop checks out on its own: said when the cart has more than one. */
const EachShopNote = ({ className }) => (
  <p className={className}>
    <InfoIcon />
    <span>
      <FormattedMessage id="CartPage.eachShopNote" />
    </span>
  </p>
);

const shopNameOf = shop => userDisplayNameAsString(shop?.author, '');

export const CartPageComponent = props => {
  const {
    intl,
    scrollingDisabled,
    shops = [],
    summary,
    currentUser,
    loading,
    fetchListingError,
    onInitializeCardPaymentData,
    callSetInitialValues,
    getListing,
    history,
    removeItemFromCartInProgress = [],
    saveForLaterInProgress = [],
    cartSaveError = null,
    onSaveCartQuantity,
    onRemoveFromCart,
    onSaveForLater,
  } = props;
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage({ id: 'CartPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage(
    { id: 'CartPage.schemaDescription' },
    { marketplaceName }
  );

  const money = amount => (amount ? formatMoney(intl, amount) : '');

  const handleOrderSubmit = shop => {
    // A seller on vacation sells nothing (the server refuses it too).
    if (sellerVacationOfGroup(shop.listings) || shop.cartItems.length === 0) return;
    // Checkout opens on the shop's first item that can be bought, with all of them
    const [mainItem] = shop.cartItems;
    const listing = mainItem.listing;
    // handleSubmit navigates to checkout itself; it is not a redux action.
    handleSubmit({
      params: { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      history,
      routes: routeConfiguration,
      currentUser,
      callSetInitialValues,
      getListing,
      cartItems: shop.cartItems,
      onInitializeCardPaymentData,
    })({
      quantity: mainItem.stockCount,
      deliveryMethod: getDeliveryMethod(listing),
      fromCart: true,
    });
  };

  // After an item leaves the cart, focus goes to the next item's title, or the page's heading.
  const rowIds = shops.flatMap(shop => shop.rows.map(row => row.id));
  const focusAfterLeaving = id => {
    const nextId = rowIds[rowIds.indexOf(id) + 1];
    const target =
      (nextId && document.getElementById(titleIdOf(nextId))) || document.getElementById(HEADING_ID);
    if (target) target.focus();
  };
  const leaveCart = (action, id) =>
    Promise.resolve(action(id)).then(left => {
      if (left) focusAfterLeaving(id);
      return left;
    });

  const hasShops = shops.length > 0;
  const buyableShops = summary?.shops || [];
  const units = summary?.units || 0;
  const manyShops = buyableShops.length > 1;

  const countText =
    buyableShops.length === 1
      ? intl.formatMessage(
          { id: 'CartPage.subtitleOneShop' },
          { items: units, shop: shopNameOf(buyableShops[0]) }
        )
      : intl.formatMessage(
          { id: 'CartPage.subtitle' },
          { items: units, shops: buyableShops.length }
        );
  const subtitle =
    !loading && hasShops && units > 0 ? (
      <>
        <span>{countText}</span>
        {summary?.total ? (
          <span className={css.subtitleTotal}>
            {intl.formatMessage({ id: 'CartPage.subtitleTotal' }, { total: money(summary.total) })}
          </span>
        ) : null}
      </>
    ) : null;

  const shopCards = shops.map(shop => {
    const shopName = shopNameOf(shop);
    const slug = profileSlug(shop.author);
    const away = sellerVacationOfGroup(shop.listings);
    const awayNoteId = `cart-shop-away-${shop.id}`;
    const shopNameNode = <span className={css.shopName}>{shopName}</span>;
    return (
      <Card key={shop.id} rootClassName={css.shop}>
        <div className={css.shopHead}>
          <h2 className={css.soldBy}>
            <FormattedMessage
              id="CartPage.soldBy"
              values={{
                shop: slug ? (
                  <NamedLink
                    className={css.shopNameLink}
                    name="ProfilePage"
                    params={{ username: slug }}
                  >
                    {shopNameNode}
                  </NamedLink>
                ) : (
                  shopNameNode
                ),
              }}
            />
          </h2>
          {slug ? (
            <NamedLink className={css.viewShop} name="ProfilePage" params={{ username: slug }}>
              <FormattedMessage id="CartPage.viewShop" />
            </NamedLink>
          ) : null}
        </div>
        <ul className={css.items}>
          {shop.rows.map(row => (
            <li className={css.item} key={row.id}>
              <CartCard
                listing={row.listing}
                quantity={row.quantity}
                purchasable={row.purchasable}
                titleId={titleIdOf(row.id)}
                renderSizes={IMAGE_SIZES}
                removeInProgress={removeItemFromCartInProgress.includes(row.id)}
                saveForLaterInProgress={saveForLaterInProgress.includes(row.id)}
                saveFailed={cartSaveError?.id === row.id}
                onChangeQuantity={quantity => onSaveCartQuantity(row.id, quantity)}
                onRemove={() => leaveCart(onRemoveFromCart, row.id)}
                onSaveForLater={() => leaveCart(onSaveForLater, row.id)}
              />
            </li>
          ))}
        </ul>
        <div className={css.shopFooter}>
          <SellerAwayNote id={awayNoteId} vacation={away} name={shopName} intl={intl} />
          <div className={css.shopTotals}>
            <p className={css.shopSubtotal}>
              <span className={css.shopSubtotalLabel}>
                <FormattedMessage id="CartPage.shopSubtotal" />
              </span>
              <span className={css.shopSubtotalAmount}>{money(shop.subtotal)}</span>
            </p>
            <p className={css.shopShippingNote}>
              <FormattedMessage id="CartPage.shippingAndTaxAtCheckout" />
            </p>
          </div>
          <CartButton
            onSubmit={() => handleOrderSubmit(shop)}
            disabled={!!away || shop.units === 0}
            label={intl.formatMessage({ id: 'CartPage.checkoutButton' })}
            ariaLabel={intl.formatMessage({ id: 'CartPage.checkout' }, { name: shopName })}
            ariaDescribedBy={away ? awayNoteId : null}
          />
        </div>
      </Card>
    );
  });

  const summaryCard = (
    <div className={css.summaryCard}>
      <h2 className={css.summaryTitle}>
        <FormattedMessage id="CartPage.orderSummary" />
      </h2>
      {manyShops ? (
        <>
          <ul className={css.summaryShops}>
            {buyableShops.map(shop => (
              <li className={css.summaryRow} key={shop.id}>
                <span className={css.summaryShopName}>{shopNameOf(shop)}</span>
                <span>{money(shop.subtotal)}</span>
              </li>
            ))}
          </ul>
          <hr className={css.summaryDivider} />
        </>
      ) : null}
      <div className={css.summaryRow}>
        <span>
          <FormattedMessage id="CartPage.items" values={{ count: units }} />
        </span>
        <span className={css.summaryAmount}>{money(summary?.total)}</span>
      </div>
      <div className={css.summaryRow}>
        <span>
          <FormattedMessage id="CartPage.shipping" />
        </span>
        <span className={css.summaryMuted}>
          <FormattedMessage id="CartPage.shippingAtCheckout" />
        </span>
      </div>
      {manyShops ? <EachShopNote className={css.note} /> : null}
    </div>
  );

  const protectionCard = (
    <div className={css.protection}>
      <ShieldCheckIcon />
      <div className={css.protectionText}>
        <p className={css.protectionTitle}>
          <FormattedMessage id="CartPage.protectionTitle" />
        </p>
        <p className={css.protectionBody}>
          <FormattedMessage id="CartPage.protectionText" values={{ marketplaceName }} />{' '}
          <NamedLink name="PurchaseProtectionPage" className={css.protectionLink}>
            <FormattedMessage id="CartPage.protectionLink" />
          </NamedLink>
        </p>
      </div>
    </div>
  );

  // One state at a time under the header: loading, error, empty, or the shops.
  const content = loading ? (
    <LoadingPlaceholder variant="rows" count={3} />
  ) : fetchListingError ? (
    <Notice tone="error" title={intl.formatMessage({ id: 'CartPage.loadErrorTitle' })}>
      <p>
        <FormattedMessage id="CartPage.loadErrorText" />
      </p>
    </Notice>
  ) : !hasShops ? (
    <EmptyState
      icon={<BagIcon />}
      title={intl.formatMessage({ id: 'CartPage.emptyTitle' })}
      text={intl.formatMessage({ id: 'CartPage.emptyText' })}
      action={
        <NamedLink name="SearchPage" className={css.primaryAction}>
          <FormattedMessage id="CartPage.browse" />
        </NamedLink>
      }
    />
  ) : (
    <>
      {manyShops ? <EachShopNote className={css.noteNarrow} /> : null}
      <div className={css.layout}>
        <div className={css.shops}>{shopCards}</div>
        <aside className={css.aside}>
          {summaryCard}
          {protectionCard}
        </aside>
      </div>
      <NamedLink name="SearchPage" className={css.continueLinkEnd}>
        <FormattedMessage id="CartPage.continueShopping" />
      </NamedLink>
    </>
  );

  return (
    <Page title={schemaTitle} description={schemaDescription} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn greyBackground topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <PageContainer>
          <PageHeader
            title={intl.formatMessage({ id: 'CartPage.title' })}
            titleProps={{ id: HEADING_ID, tabIndex: -1 }}
            subtitle={subtitle}
            actions={
              !loading && hasShops ? (
                <NamedLink name="SearchPage" className={css.continueLink}>
                  <FormattedMessage id="CartPage.continueShopping" />
                </NamedLink>
              ) : null
            }
          />
          {content}
        </PageContainer>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    fetchListingInProgress,
    fetchListingRequest,
    fetchListingError,
    removeItemFromCartInProgress,
    saveForLaterInProgress,
    cartSaveError,
  } = state.CartPage;
  // Loading until the cart's first load answers, not only while it runs.
  const loading = !!fetchListingInProgress || fetchListingRequest == null;
  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };
  return {
    currentUser,
    loading,
    fetchListingError,
    scrollingDisabled: isScrollingDisabled(state),
    shops: selectCartShops(state),
    summary: selectCartSummary(state),
    removeItemFromCartInProgress,
    saveForLaterInProgress,
    cartSaveError,
    getListing,
  };
};

const mapDispatchToProps = dispatch => ({
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onSaveCartQuantity: (id, quantity) => dispatch(saveCartQuantity({ id, quantity })),
  onRemoveFromCart: id => dispatch(deleteItemFromCart({ id })),
  onSaveForLater: id => dispatch(saveItemForLater({ id })),
});

const CartPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(CartPageComponent);

export default CartPage;
